import { Divider, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  AutoComplete,
  FormContainer,
  FormEmojiTextInput,
  FormInput,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainApi from "../../../../../services/mainApi.service";
import TicketServAreaService from "../../../../../services/ticketServiceArea.service";
import { EnableAllTheTime } from "./EnableAllTheTime";
import { EnableField } from "./EnableField/EnableField";
import { EnableSpecialTime } from "./EnableSpecialTime";
import { TicketServiceAreaDays } from "./TicketServiceAreaDays/TicketServiceAreaDays";
import TicketServiceAreaField from "./TicketServiceAreaField";
import {
  TTicketServiceAreaForm,
  ticketServiceAreaSchema,
} from "./TicketServiceAreaSchema";
import { ActionSelector } from "./Actions/ActionSelector";
import { IsIdentification } from "./IsIdentificationField";
import OutsideBusinessDayField from "./OutsideBusinessDay/OutsideBusinessDayField";
import { TicketServiceField } from "./TicketServiceField";

const TICKET_SERVICE_ROUTE =
  process.env.REACT_APP_TICKETS + "/api/v1/TicketServices/";
const ticketService = new MainApi(TICKET_SERVICE_ROUTE);

const ticketServiceArea = new TicketServAreaService();

const translationPath = "chatBot.page.service.ticketServiceArea.";

const TicketServiceArea = () => {
  const navigate = useNavigate();

  const [isSpecialTime, setIsSpecialTime] = useState(false);

  const [isAllTheTime, setIsAllTheTime] = useState(false);

  const [isEnabled, setIsEnabled] = useState(true);

  const { eI, id } = useParams();

  const { t } = useTranslation();

  const { sendRequest: save, loading: sLoading } = useFetch(
    ticketServiceArea.create,
  );
  const { sendRequest: update, loading: uLoading } = useFetch(
    ticketServiceArea.updateItemById,
  );
  const { sendRequest: get, loading: gLoading } = useFetch(
    ticketServiceArea.getItemById,
  );

  const { sendRequest: activeById, loading: aLoading } = useFetch(
    ticketServiceArea.activeChildsById,
  );

  const { sendRequest: inactiveById, loading: iLoading } = useFetch(
    ticketServiceArea.inactiveChildsById,
  );

  const actionSelectorOptions = useMemo(
    () => [
      {
        id: "0",
        name: t(translationPath + "no_action"),
        code: "0",
        actionName: "",
      },
      {
        id: "1",
        name: t(translationPath + "trigger_event"),
        code: "1",
        actionName: "ticketEvent",
      },
      {
        id: "2",
        name: t(translationPath + "default_protocol"),
        code: "2",
        actionName: "ticketDefaultDoc",
      },
      {
        id: "3",
        name: t(translationPath + "channel_transfer"),
        code: "3",
        actionName: "ticketChannel",
      },
      {
        id: "4",
        name: t(translationPath + "received_rule"),
        code: "4",
        actionName: "ticketAutomationReceivedRule",
      },
      {
        id: "5",
        name:
          t(translationPath + "direct_to_internal_chat") ||
          "Direcionar para chat interno (4uChat)",
        code: "5",
        actionName: "externalChat",
      },
    ],
    [t],
  );

  const handleSave = (v: TTicketServiceAreaForm) => {
    return save({
      ...v,
      ticketChannelId: v.ticketChannel ? v.ticketChannel.id : null,
      ticketServiceAreaId: v.ticketServiceArea ? v.ticketServiceArea.id : null,
      ticketServiceId: v.ticketService.id,
      ticketDefaultDocId: v.ticketDefaultDoc ? v.ticketDefaultDoc.id : null,
      ticketChannelStepId: v.ticketChannelStep ? v.ticketChannelStep.id : null,
      displayOrder: v.displayOrder,
      ticketEventId: v.ticketEvent ? v.ticketEvent.id : undefined,
      summary: v.summary,
      ticketAutomationReceivedRuleId: v.ticketAutomationReceivedRule?.id,
      ticketChannelStepOutsideBusinessDayId:
        v.ticketChannelStepOutsideBusinessDay?.id ?? null,
      externalChatSiteAppId:
        v.actionSelector?.code === "5" && v.externalChatSiteApp
          ? v.externalChatSiteApp.id
          : null,
      fullScreenMode:
        v.actionSelector?.code === "5" ? (v.fullScreenMode ?? false) : false,
      sendProtocolNumber:
        v.actionSelector?.code === "5" ? (v.sendProtocolNumber ?? false) : false,
    });
  };

  const updateData = useCallback((v: TTicketServiceAreaForm, id: string) => {
    return {
      item: {
        ...v,
        ticketChannelId: v.ticketChannel ? v.ticketChannel.id : null,
        ticketServiceAreaId: v.ticketServiceArea
          ? v.ticketServiceArea.id
          : null,
        ticketServiceId: v.ticketService.id,
        ticketDefaultDocId: v.ticketDefaultDoc ? v.ticketDefaultDoc.id : null,
        ticketChannelStepId: v.ticketChannelStep
          ? v.ticketChannelStep.id
          : null,
        displayOrder: v.displayOrder,
        ticketEventId: v.ticketEvent ? v.ticketEvent.id : undefined,
        summary: v.summary,
        ticketAutomationReceivedRuleId: v.ticketAutomationReceivedRule?.id,
        ticketChannelStepOutsideBusinessDayId:
          v.ticketChannelStepOutsideBusinessDay
            ? v.ticketChannelStepOutsideBusinessDay.id
            : null,
        externalChatSiteAppId:
          v.actionSelector?.code === "5" && v.externalChatSiteApp
            ? v.externalChatSiteApp.id
            : null,
        fullScreenMode:
          v.actionSelector?.code === "5" ? (v.fullScreenMode ?? false) : false,
        sendProtocolNumber:
          v.actionSelector?.code === "5" ? (v.sendProtocolNumber ?? false) : false,
      },
      id: id,
    };
  }, []);

  const updateHandler = useCallback(
    async (v: TTicketServiceAreaForm, id: string) => {
      if (isEnabled !== v.enable && !v.ticketServiceArea) {
        if (v.enable) {
          const { success } = await activeById({ id });
          if (success) {
            return await update(updateData(v, id));
          }
          return { data: null, success: false };
        } else {
          const { success } = await inactiveById({ id });
          if (success) {
            return await update(updateData(v, id));
          }
          return { data: null, success: false };
        }
      }

      return await update(updateData(v, id));
    },
    [
      isEnabled,
      aLoading,
      iLoading,
      activeById,
      inactiveById,
      update,
      updateData,
    ],
  );

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TTicketServiceAreaForm>> => {
      const { data, success } = await get(id);

      console.log(data);
      if (data) {
        setIsEnabled(data.enable);

        return {
          data: {
            ...data,
            displayOrder: data?.displayOrder.toString(),
            ticketEventType: data.ticketEvent?.ticketEventType,
            actionSelector: data.ticketEvent
              ? actionSelectorOptions[1]
              : data.ticketDefaultDoc
                ? actionSelectorOptions[2]
                : data.ticketChannel
                  ? actionSelectorOptions[3]
                  : data.ticketAutomationReceivedRule
                    ? actionSelectorOptions[4]
                    : data.externalChatSiteAppId
                      ? actionSelectorOptions[5]
                      : actionSelectorOptions[0],
            externalChatSiteApp: data.externalChatSiteApp
              ? {
                  id: data.externalChatSiteApp.id,
                  name: data.externalChatSiteApp.name,
                }
              : data.externalChatSiteAppId
                ? {
                    id: data.externalChatSiteAppId,
                    name: (data as any).externalChatSiteAppName || "",
                  }
                : null,
            fullScreenMode: data.fullScreenMode ?? false,
            sendProtocolNumber: data.sendProtocolNumber ?? false,
            ticketChannelStepOutsideBusinessDay:
              data.ticketChannelStepOutsideBusinessDay ?? null,
          },
          success: success,
        };
      }
      return { data, success };
    },
    [get, actionSelectorOptions],
  );

  const saveRedirectHandler = useCallback(
    (data: any) => {
      if (!data || !data.id) return;
      if (isSpecialTime) {
        navigate(
          `/e/${eI}/chatBot/registrationsAndRules/ticketServiceArea/${data.id}/edit`,
        );
      } else {
        navigate(`/e/${eI}/chatBot/registrationsAndRules/ticketServiceArea/`);
      }
    },
    [navigate, eI, isSpecialTime],
  );

  const isSpecialTimeHandler = useCallback((value: boolean) => {
    setIsSpecialTime(value);
  }, []);

  const isAllTheTimeHandler = useCallback((value: boolean) => {
    setIsAllTheTime(value);
  }, []);

  const loading = sLoading || uLoading || gLoading || aLoading || iLoading;

  return (
    <>
      <FormContainer<TTicketServiceAreaForm>
        saveFunction={handleSave}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "ticket_service_area")}
        schema={ticketServiceAreaSchema}
        redirectOnSave={!id ? false : true}
        onSuccess={!id ? saveRedirectHandler : undefined}
        backUrl={`/e/${eI}/chatBot/registrationsAndRules/ticketServiceArea`}
        customHeight={"fit-content"}
        subtitle={t(translationPath + "new_ticket_service_area")}
        subtitleWatchField={"name"}
        defaultValues={{
          enableAllTheTime: false,
          enableSpecialTime: false,
          fullScreenMode: false,
          sendProtocolNumber: false,
        }}
        fields={() => (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} textAlign={"end"}>
              <EnableField currentValue={isEnabled} />
              <IsIdentification />
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormInput
                size="small"
                name="code"
                fullWidth
                label={t(translationPath + "code") || "Code"}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormInput
                size="small"
                name="displayOrder"
                type="number"
                fullWidth
                label={t(translationPath + "display_order") || "Display order"}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormEmojiTextInput
                size="small"
                name="name"
                fullWidth
                label={t(translationPath + "name") || "Name"}
              />
            </Grid>

            <TicketServiceField request={ticketService.list} />

            <TicketServiceAreaField />

            <Grid item xs={12} sm={12}>
              <FormEmojiTextInput
                size="small"
                name="summary"
                fullWidth
                label={t(translationPath + "summary") || "Summary"}
              />
            </Grid>

            <Grid item xs={12} sm={12}>
              <Divider sx={{ width: "100%", my: 2 }} />
              <Typography fontWeight="bold">
                {t(translationPath + "actions")}
              </Typography>
            </Grid>

            <ActionSelector options={actionSelectorOptions} />

            <Grid item xs={12} sm={12}>
              <Divider sx={{ width: "100%", my: 2 }} />
              <Typography fontWeight="bold">
                {t(translationPath + "time")}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={12}>
              <EnableAllTheTime onIsAllTheTime={isAllTheTimeHandler} />
              <EnableSpecialTime onIsSpecialTime={isSpecialTimeHandler} />
            </Grid>

            <OutsideBusinessDayField isAllTheTime={isAllTheTime} />

            {/* respiro para o último campo não ficar sob a barra Salvar/Cancelar */}
            <Grid item xs={12} sx={{ height: 80 }} />
          </Grid>
        )}
      />
      {isSpecialTime && <TicketServiceAreaDays />}
    </>
  );
};

export default TicketServiceArea;
